import { useEffect, useState } from 'react';
import {
	add,
	getLobOptions,
	getNaicOptions,
	ImportTypeInsuranceCarrier,
	ImportTypeInsuranceCarrierSchema,
} from '@/api/admin/insurance-carrier';
import { Button } from '@/components/ui/button';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';
import { Form, FormField } from '@/components/ui/form';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddImportTypeInsuranceCarrier({
	importTypeId,
}: {
	importTypeId: number;
}) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (newImportTypeInsuranceCarrier: ImportTypeInsuranceCarrier) =>
			add(newImportTypeInsuranceCarrier),
		onSuccess: () => {
			toast.success(`Success! Insurance Carrier has been successfully added.`, {
				dismissible: true,
			});
			setOpen(false);

			//Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [`insuranceCarriers-${importTypeId}`],
			});
			queryClient.invalidateQueries({
				queryKey: [`naic-options`],
			});

			reset();
		},
		onError: () => {
			toast.error(`Error! Insurance Carrier could not be added.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<ImportTypeInsuranceCarrier>({
		resolver: zodResolver(ImportTypeInsuranceCarrierSchema),
		mode: 'all',
	});

	const { reset, watch } = form;

	const { data: lobOptions, isLoading: isLoadingLobOptions } = useQuery({
		queryKey: [`lob-options`, importTypeId],
		queryFn: () => getLobOptions(importTypeId),
		staleTime: 5 * 60 * 1000,
	});

	const selectedLobId = watch('importTypeLobId');
	const { data: naicOptions, isLoading: isLoadingNaicOptions } = useQuery({
		queryKey: [`naic-options`, selectedLobId],
		queryFn: () => getNaicOptions(selectedLobId!),
		staleTime: 5 * 60 * 1000,
		enabled: !!selectedLobId,
	});

	useEffect(() => {
		form.resetField('naicId');
	}, [selectedLobId, form]);

	const onSubmit: SubmitHandler<ImportTypeInsuranceCarrier> = (
		values: ImportTypeInsuranceCarrier,
	) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" onClick={() => setOpen(true)}>
							<CirclePlus />
							Add New Insurance Carrier
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Add a new insurance carrier</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Add a New Insurance Carrier</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="importTypeLobId"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={lobOptions}
											valueKey="value"
											displayKey="label"
											isLoading={isLoadingLobOptions}
											label="Import Type LOB"
											placeholder="Select import type LOB..."
											searchPlaceholder="Search import type LOB..."
											required={true}
											emptyMessage="No import type LOB found."
											loadingMessage="Loading import type LOB..."
										/>
									)}
								/>
								<FormField
									control={form.control}
									name="naicId"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={naicOptions}
											valueKey="value"
											displayKey="label"
											isLoading={isLoadingNaicOptions}
											label="Company Name"
											placeholder={
												selectedLobId
													? 'Select company name...'
													: 'Select an Import Type LOB first'
											}
											searchPlaceholder="Search company name..."
											required={true}
											emptyMessage="No company name found."
											loadingMessage="Loading company name..."
											disabled={!selectedLobId}
										/>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Adding...' : 'Add'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
